import React, {useCallback} from 'react';

import {Badge, Text, WithTooltip} from '@albert-io/atomic';
import EnrolledStudentsActionsDropdown from 'client/Classrooms/Classroom/Teacher/ManageRoster/EnrolledStudents/EnrolledStudentsActionsDropdown/EnrolledStudentsActionsDropdown.react';
import {getStudentsQuery} from 'client/Classrooms/Classroom/Teacher/People/people.queries';

import {ErrorRetryState} from '../../components/ErrorRetryState';
import {InviteMethodCards} from './InviteMethodCards';
import {PeopleEmptyState} from './PeopleEmptyState';
import {RosterTable} from './RosterTable';
import type {RosterColumn} from './RosterTable';
import {ROSTER_PAGE_SIZE, useRosterPagination} from './useRosterPagination';
import {useSortState} from './useSortState';
import type {SortDir} from './useSortState';
import type {RosterClassroom, RosterStudent} from './usePeopleRoster';

// Sortable columns → real Mandark sort fields (mirrors the legacy EnrolledStudents table's sortBy).
const STUDENT_SORT_FIELD: Record<string, string> = {
  last: 'last_name',
  first: 'first_name',
  email: 'email'
};

const toSortParam = (key: string, dir: SortDir): string =>
  `${dir === 'desc' ? '-' : ''}${STUDENT_SORT_FIELD[key] ?? 'last_name'}`;

interface Props {
  classroom: RosterClassroom;
  search: string;
  onShowModal: (modal: string) => void;
}

/**
 * Enrolled students, server-paged (25/page, load-on-scroll). Search and sort are Mandark query params —
 * matching the legacy view — so they span the whole roster, not just the loaded page. Row actions
 * and the getting-started invite cards are the reused legacy flows.
 */
export const EnrolledList = ({classroom, search, onShowModal}: Props) => {
  const classId = classroom.getId();
  const {sortKey, sortDir, toggleSort} = useSortState('last');

  const buildQuery = useCallback(
    (page: number) => {
      let query = getStudentsQuery(classId)
        .pageSize(ROSTER_PAGE_SIZE)
        .sort(toSortParam(sortKey, sortDir))
        .page(page);
      // Multi-term: each term must match first/last/email (any_of), terms AND together (all_of).
      const terms = search.trim().split(' ').filter(Boolean);
      terms.forEach((term) => {
        const encoded = encodeURIComponent(term);
        query = query.filter({
          all_of: [
            {
              any_of: [
                {first_name: {case_insensitive_substring: encoded}},
                {last_name: {case_insensitive_substring: encoded}},
                {email: {case_insensitive_substring: encoded}}
              ]
            }
          ]
        });
      });
      return query;
    },
    [classId, search, sortKey, sortDir]
  );

  const {rows, isLoading, isLoadingMore, hasNextPage, hasError, loadMore, refetch} =
    useRosterPagination<RosterStudent>({buildQuery, label: 'students'});

  // Whole-roster empty states (parity with the legacy EnrolledStudents view), read from the classroom
  // count meta rather than a full fetch.
  if (classroom.getCountOfStudents() === 0) {
    if (classroom.getCountOfPendingClassroomInvitations() === 0) {
      return <InviteMethodCards onShowModal={onShowModal} />;
    }
    return (
      <PeopleEmptyState
        body='Not all students have accepted their invitations. To re-send invitations, view the Invited section.'
        icon='envelope-circle-user'
        title='No enrolled students'
      />
    );
  }

  if (hasError) {
    return <ErrorRetryState copy='We couldn’t load this class’s students.' onRetry={refetch} />;
  }

  // getSchool() can be null; degrade the copy rather than crash.
  const schoolName = classroom.getSchool()?.getName() ?? 'your school';
  const hasManagedStudents = rows.some((student) => student.getMeta().isManagedUser());

  const managedColumn: RosterColumn<RosterStudent> = {
    key: 'managed',
    headerClassName: 'w-x6',
    cellClassName: 'px-x2 py-x1-half text-center',
    render: (student) =>
      student.getMeta().isManagedUser() ? (
        <WithTooltip content={`Managed by ${schoolName}`} focusable>
          <Badge emphasis='bold' icon={['far', 'school']} iconAlign='center' />
        </WithTooltip>
      ) : null
  };

  const columns: RosterColumn<RosterStudent>[] = [
    {
      key: 'last',
      label: 'Last Name',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half',
      render: (student) => <Text>{student.getLastName()}</Text>
    },
    {
      key: 'first',
      label: 'First Name',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half',
      render: (student) => <Text>{student.getFirstName()}</Text>
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      cellClassName: 'px-x2 py-x1-half break-all',
      render: (student) => <Text>{student.getEmail()}</Text>
    },
    ...(hasManagedStudents ? [managedColumn] : []),
    {
      key: 'actions',
      headerClassName: 'w-x6',
      srOnlyHeader: 'Actions',
      cellClassName: 'px-x2 py-x1-half text-right',
      render: (student) => (
        <EnrolledStudentsActionsDropdown classroom={classroom} student={student} />
      )
    }
  ];

  return (
    <RosterTable
      ariaLabel='Enrolled students'
      columns={columns}
      getRowKey={(student) => student.getId()}
      hasNextPage={hasNextPage}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      noResultsMessage='No students match your search.'
      onShowMore={loadMore}
      onSort={toggleSort}
      rows={rows}
      sortDir={sortDir}
      sortKey={sortKey}
    />
  );
};
